import React, { useMemo } from 'react'
import { connect } from 'react-redux'
import { useIntl } from 'react-intl'

import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import type { OfferListEventsDispatchAction } from 'components/OfferList/OfferListEventsContext'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import HotelOfferCarousel from 'home/components/OfferCarousels/HotelOfferCarousel'
import useTPFMOfferList from 'hooks/Recommendation/useTPFMOfferList'
import { encodeSearchParams } from 'lib/search/searchUtils'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import { selectLoggedIn } from 'selectors/accountSelectors'
import styled from 'styled-components'
import OfferCarouselLookingForSomethingElse from '../LookingForSomethingElse/OfferCarouselLookingForSomethingElse'

const CarouselContainer = styled(Pane)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

interface Props {
  userGivenName?: string
  viewAllUrl?: string
  isLoggedIn: boolean
  onHotelListEvent: (dispatchAction: OfferListEventsDispatchAction) => void
  titleVariant?: React.ComponentProps<typeof Heading>['variant']
  overrideTitle?: React.ReactNode
  overrideOfferList?: App.OfferList
  excludedIds?: Array<string>
}

/**
 * Displays personalized offers if 9+ are available, otherwise shows hotel carousel
 */
function TopPicksForMeCarousel({
  userGivenName,
  viewAllUrl,
  isLoggedIn,
  onHotelListEvent,
  titleVariant = 'heading2',
  overrideTitle,
  overrideOfferList,
  excludedIds,
}: Props) {
  const intl = useIntl()
  const tpfmOfferList = useTPFMOfferList({ excludedIds })

  const title = useMemo(() => {
    if (overrideTitle) {
      return overrideTitle
    }
    if (userGivenName) {
      return intl.formatMessage(
        {
          id: 'wmDELD_mHZ44_yrm-sQ7',
          defaultMessage: 'Top picks for <i>{name}</i>',
          description: 'Title for top picks carousel with italicized user name',
        },
        {
          name: userGivenName,
          i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
        },
      )
    }
    return intl.formatMessage(
      {
        id: '73XUtIrEmn-q89K-qdpD',
        defaultMessage: 'Top picks for <i>you</i>',
        description: 'Title for top picks carousel when user name is not available',
      },
      {
        i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
      },
    )
  }, [intl, overrideTitle, userGivenName])
  const hideEditPreferences = !!overrideOfferList

  const searchUrl = useMemo(() => {
    const query = encodeSearchParams({
      searchItem: ANYWHERE_SEARCH_ITEM,
      rooms: [config.search.defaultOccupants],
    })
    return `/search?${query.toString()}`
  }, [])

  if (!tpfmOfferList.fetching && tpfmOfferList.offerIds.length < 9) {
    return (
      <OfferListEventsProvider onListEvent={onHotelListEvent}>
        <HotelOfferCarousel viewAllUrl={viewAllUrl} />
      </OfferListEventsProvider>
    )
  }

  return (
    <CarouselContainer>
      <VerticalSpacer gap={24}>
        <LayoutContainer>
          <VerticalSpacer gap={16}>
            <Group
              direction="horizontal"
              horizontalAlign="space-between"
              verticalAlign="center"
              gap={16}
            >
              <Group direction="horizontal" gap={16} verticalAlign="center">
                <VerticalSpacer gap={2}>
                  <Heading variant={titleVariant}>{title}</Heading>
                  {isLoggedIn && !hideEditPreferences && (
                    <TextLink to="/account/travel-preferences" size="medium" weight="regular">
                      {intl.formatMessage({
                        id: 'rBZS9u5bUHdcsJtWq3jr',
                        defaultMessage: 'Edit your preferences',
                        description: 'Hyperlink text to edit travel preferences on the homepage',
                      })}
                    </TextLink>
                  )}
                </VerticalSpacer>
              </Group>
              <Group direction="horizontal" gap={16} verticalAlign="center">
                <TextButton
                  kind="tertiary"
                  to={searchUrl}
                >
                  {intl.formatMessage({
                    id: 'XMHO6v1XbawuUzHKS0Pl',
                    defaultMessage: 'View all',
                    description: 'Button text to view all offers on the offer listing or detail page',
                  })}
                </TextButton>
              </Group>
            </Group>
          </VerticalSpacer>
        </LayoutContainer>
        <OfferListCarousel
          overrideOfferList={overrideOfferList ?? tpfmOfferList}
          tabletLimit="default"
          tileStyle="card-default"
          tileSize="medium"
        >
          <OfferCarouselLookingForSomethingElse to={searchUrl} />
        </OfferListCarousel>
      </VerticalSpacer>
    </CarouselContainer>
  )
}

function mapState(state: App.State) {
  return {
    userGivenName: state.auth.account.givenName,
    isLoggedIn: selectLoggedIn(state),
  }
}

export default connect(mapState)(TopPicksForMeCarousel)
